import React from "react";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertCircle, Shield, Info, ChevronDown, ChevronUp } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { LocationSelector } from "@/components/targeting";
import type { SelectedLocation, SpecialAdCategory } from "@/types/geoTargeting";
import { formatGeoTargeting } from "@/utils/formatGeoTargeting";
import { Switch } from "@/components/ui/switch";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCachedSavedAudiences } from "@/hooks/useCachedSavedAudiences";
import { useQuery } from "@tanstack/react-query";

// EU country codes for DSA compliance detection
const EU_COUNTRY_CODES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "GF",
  "GP",
  "MQ",
  "RE",
  "YT",
  "MF",
  "PM",
  "BL",
  "NC",
  "PF",
  "WF",
];

// ── Conversion events allowed per objective ──
const CONVERSION_EVENTS_BY_OBJECTIVE: Record<string, { events: string[]; default: string }> = {
  OUTCOME_SALES: {
    events: [
      "PURCHASE",
      "ADD_TO_CART",
      "INITIATED_CHECKOUT",
      "ADD_PAYMENT_INFO",
      "ADD_TO_WISHLIST",
      "CONTENT_VIEW",
      "COMPLETE_REGISTRATION",
      "START_TRIAL",
      "SUBSCRIBE",
      "SEARCH",
    ],
    default: "PURCHASE",
  },
  OUTCOME_LEADS: {
    events: [
      "LEAD",
      "COMPLETE_REGISTRATION",
      "CONTACT",
      "SUBMIT_APPLICATION",
      "FIND_LOCATION",
      "SCHEDULE",
      "START_TRIAL",
      "SEARCH",
      "CONTENT_VIEW",
    ],
    default: "LEAD",
  },
  OUTCOME_ENGAGEMENT: {
    events: [
      "CUSTOMIZE_PRODUCT",
      "ADD_TO_WISHLIST",
      "CONTACT",
      "DONATE",
      "FIND_LOCATION",
      "SCHEDULE",
      "START_TRIAL",
      "SUBSCRIBE",
      "SUBMIT_APPLICATION",
      "SEARCH",
      "CONTENT_VIEW",
    ],
    default: "CONTENT_VIEW",
  },
};

const CONVERSION_EVENT_LABELS: Record<string, string> = {
  PURCHASE: "Purchase",
  ADD_TO_CART: "Add to cart",
  INITIATED_CHECKOUT: "Initiate checkout",
  ADD_PAYMENT_INFO: "Add payment info",
  ADD_TO_WISHLIST: "Add to wishlist",
  CONTENT_VIEW: "View content",
  COMPLETE_REGISTRATION: "Complete registration",
  START_TRIAL: "Start trial",
  SUBSCRIBE: "Subscribe",
  SEARCH: "Search",
  LEAD: "Lead",
  CONTACT: "Contact",
  SUBMIT_APPLICATION: "Submit application",
  FIND_LOCATION: "Find location",
  SCHEDULE: "Schedule",
  CUSTOMIZE_PRODUCT: "Customise product",
  DONATE: "Donate",
};

// ── Conversion Location options by objective ──
const CONVERSION_LOCATION_OPTIONS: Record<string, Array<{ label: string; value: string }>> = {
  OUTCOME_AWARENESS: [],
  OUTCOME_TRAFFIC: [
    { label: "Website", value: "WEBSITE" },
    { label: "Messaging Apps", value: "MESSENGER" },
    { label: "Instagram Profile", value: "INSTAGRAM_PROFILE" },
    { label: "Calls", value: "PHONE_CALL" },
  ],
  OUTCOME_ENGAGEMENT: [
    { label: "On Your Ad — Video Views", value: "ON_VIDEO" },
    { label: "On Your Ad — Post Engagement", value: "ON_POST" },
    { label: "On Your Ad — Event Responses", value: "ON_EVENT" },
    { label: "On Your Ad — Reminders Set", value: "ON_REMINDER" },
    { label: "Website", value: "WEBSITE" },
    { label: "Instagram or Facebook — Page Likes", value: "ON_PAGE" },
  ],
  OUTCOME_LEADS: [
    { label: "Instant Forms", value: "ON_AD" },
    { label: "Website", value: "WEBSITE" },
    { label: "Messaging Apps", value: "MESSENGER" },
    { label: "Calls", value: "PHONE_CALL" },
  ],
  OUTCOME_SALES: [
    { label: "Website", value: "WEBSITE" },
    { label: "Messaging Apps", value: "MESSENGER" },
    { label: "Calls", value: "PHONE_CALL" },
  ],
};

// ── Performance Goal options by objective + conversion location ──
const PERFORMANCE_GOAL_OPTIONS: Record<string, Record<string, Array<{ label: string; value: string }>>> = {
  OUTCOME_AWARENESS: {
    _default: [
      { label: "Maximise ad recall lift", value: "AD_RECALL_LIFT" },
      { label: "Maximise reach", value: "REACH" },
      { label: "Maximise number of impressions", value: "IMPRESSIONS" },
      { label: "Maximise ThruPlay views", value: "THRUPLAY" },
      { label: "Maximise 2-second continuous video views", value: "TWO_SECOND_CONTINUOUS_VIDEO_VIEWS" },
    ],
  },
  OUTCOME_TRAFFIC: {
    WEBSITE: [
      { label: "Maximise number of landing page views", value: "LANDING_PAGE_VIEWS" },
      { label: "Maximise number of link clicks", value: "LINK_CLICKS" },
      { label: "Maximise daily unique reach", value: "REACH" },
      { label: "Maximise number of impressions", value: "IMPRESSIONS" },
    ],
    MESSENGER: [{ label: "Maximise number of conversations", value: "CONVERSATIONS" }],
    INSTAGRAM_PROFILE: [{ label: "Maximise profile visits", value: "VISIT_INSTAGRAM_PROFILE" }],
    PHONE_CALL: [{ label: "Maximise number of calls", value: "QUALITY_CALL" }],
  },
  OUTCOME_ENGAGEMENT: {
    ON_VIDEO: [
      { label: "Maximise ThruPlay views", value: "THRUPLAY" },
      { label: "Maximise 2-second continuous video views", value: "TWO_SECOND_CONTINUOUS_VIDEO_VIEWS" },
    ],
    ON_POST: [
      { label: "Maximise engagement with a post", value: "POST_ENGAGEMENT" },
      { label: "Maximise daily unique reach", value: "REACH" },
      { label: "Maximise number of impressions", value: "IMPRESSIONS" },
    ],
    ON_EVENT: [
      { label: "Maximise number of event responses", value: "EVENT_RESPONSES" },
      { label: "Maximise engagement with a post", value: "POST_ENGAGEMENT" },
      { label: "Maximise daily unique reach", value: "REACH" },
      { label: "Maximise number of impressions", value: "IMPRESSIONS" },
    ],
    ON_REMINDER: [{ label: "Maximise reminders set", value: "REMINDERS_SET" }],
    WEBSITE: [
      { label: "Maximise number of link clicks", value: "LINK_CLICKS" },
      { label: "Maximise number of landing page views", value: "LANDING_PAGE_VIEWS" },
      { label: "Maximise number of conversions", value: "OFFSITE_CONVERSIONS" },
    ],
    ON_PAGE: [
      { label: "Maximise number of page likes", value: "PAGE_LIKES" },
    ],
  },
  OUTCOME_LEADS: {
    ON_AD: [
      { label: "Maximise number of leads", value: "LEAD_GENERATION" },
      { label: "Maximise number of conversion leads", value: "QUALITY_LEAD" },
    ],
    WEBSITE: [
      { label: "Maximise number of conversions", value: "OFFSITE_CONVERSIONS" },
      { label: "Maximise number of leads", value: "LEAD_GENERATION" },
      { label: "Maximise number of landing page views", value: "LANDING_PAGE_VIEWS" },
      { label: "Maximise number of link clicks", value: "LINK_CLICKS" },
    ],
    MESSENGER: [
      { label: "Maximise number of leads", value: "LEAD_GENERATION" },
      { label: "Maximise number of conversations", value: "CONVERSATIONS" },
    ],
    PHONE_CALL: [{ label: "Maximise number of calls", value: "QUALITY_CALL" }],
  },
  OUTCOME_SALES: {
    WEBSITE: [
      { label: "Maximise number of conversions", value: "OFFSITE_CONVERSIONS" },
      { label: "Maximise value of conversions", value: "VALUE" },
      { label: "Maximise number of landing page views", value: "LANDING_PAGE_VIEWS" },
      { label: "Maximise number of link clicks", value: "LINK_CLICKS" },
      { label: "Maximise daily unique reach", value: "REACH" },
      { label: "Maximise number of impressions", value: "IMPRESSIONS" },
    ],
    MESSENGER: [
      { label: "Maximise number of conversations", value: "CONVERSATIONS" },
      { label: "Maximise messaging purchase conversions", value: "MESSAGING_PURCHASE_CONVERSION" },
    ],
    PHONE_CALL: [{ label: "Maximise number of calls", value: "QUALITY_CALL" }],
  },
};

interface DsaRecommendation {
  name: string;
}

interface Campaign {
  id: string;
  name: string;
  budget_type: "CBO" | "Ad Set Budget" | null;
}

interface CustomAudience {
  id: string;
  name: string;
  display_name: string;
}

interface Pixel {
  id: string;
  name: string;
}

const ALL_PLACEMENTS = [
  // Facebook
  { id: "facebook_feed", label: "Facebook Feed", platform: "facebook" },
  { id: "facebook_profile_feed", label: "Facebook profile feed", platform: "facebook" },
  { id: "facebook_marketplace", label: "Facebook Marketplace", platform: "facebook" },
  { id: "facebook_right_column", label: "Facebook right column", platform: "facebook" },
  { id: "facebook_business_explore", label: "Facebook Business Explore", platform: "facebook" },
  { id: "facebook_notifications", label: "Facebook notifications", platform: "facebook" },
  { id: "facebook_stories", label: "Facebook Stories", platform: "facebook" },
  { id: "facebook_reels", label: "Facebook Reels", platform: "facebook" },
  { id: "facebook_instream_reels", label: "Facebook in-stream reels", platform: "facebook" },
  { id: "facebook_search_results", label: "Facebook search results", platform: "facebook" },
  // Instagram
  { id: "instagram_feed", label: "Instagram Feed", platform: "instagram" },
  { id: "instagram_profile_feed", label: "Instagram profile feed", platform: "instagram" },
  { id: "instagram_explore", label: "Instagram Explore", platform: "instagram" },
  { id: "instagram_explore_home", label: "Instagram Explore home", platform: "instagram" },
  { id: "instagram_stories", label: "Instagram Stories", platform: "instagram" },
  { id: "instagram_reels", label: "Instagram Reels", platform: "instagram" },
  { id: "instagram_profile_reels", label: "Instagram profile reels", platform: "instagram" },
  { id: "instagram_search_results", label: "Instagram search results", platform: "instagram" },
  // Messenger
  { id: "messenger_inbox", label: "Messenger inbox", platform: "messenger" },
  { id: "messenger_stories", label: "Messenger Stories", platform: "messenger" },
  // Threads
  { id: "threads_feed", label: "Threads feed", platform: "threads" },
  // Audience Network
  {
    id: "audience_network_native",
    label: "Audience Network native, banner and interstitial",
    platform: "audience_network",
  },
  { id: "audience_network_rewarded", label: "Audience Network rewarded videos", platform: "audience_network" },
];

const CreateAdSet = () => {
  const { user } = useAuth();
  const { selectedAdAccountId, selectedAdAccountName } = useAdAccount();
  const { toast } = useToast();

  // Loading states
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [loadingPixels, setLoadingPixels] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [customAudiences, setCustomAudiences] = useState<CustomAudience[]>([]);
  const [pixels, setPixels] = useState<Pixel[]>([]);

  // Section 1: Basic Information
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [adSetName, setAdSetName] = useState("");
  const [status, setStatus] = useState("PAUSED");

  // Section 2: Conversion & Performance
  const [campaignObjective, setCampaignObjective] = useState<string | null>(null);
  const [conversionLocation, setConversionLocation] = useState<string>("");
  const [selectedPixel, setSelectedPixel] = useState("");
  const [customEventType, setCustomEventType] = useState("PURCHASE");

  // ── Derived: allowed conversion events for current objective ──
  const allowedConversionEvents = useMemo(() => {
    if (!campaignObjective) return null;
    return CONVERSION_EVENTS_BY_OBJECTIVE[campaignObjective] || null;
  }, [campaignObjective]);
  const [performanceGoal, setPerformanceGoal] = useState("");
  const [selectedPageId, setSelectedPageId] = useState<string>("");

  // Section 3: Targeting
  const [selectedLocations, setSelectedLocations] = useState<SelectedLocation[]>([]);
  const [specialAdCategory, setSpecialAdCategory] = useState<SpecialAdCategory>(null);
  const [allowedCountries, setAllowedCountries] = useState<string[]>([]);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [genders, setGenders] = useState("All");
  const [includeAudiences, setIncludeAudiences] = useState<string[]>([]);
  const [excludeAudiences, setExcludeAudiences] = useState<string[]>([]);
  const [selectedSavedAudienceId, setSelectedSavedAudienceId] = useState<string | null>(null);

  // Fetch saved audiences using the cache-first hook
  const { data: savedAudiences, isLoading: loadingSavedAudiences } = useCachedSavedAudiences(
    user?.id,
    selectedAdAccountId || undefined,
  );

  const isSavedAudienceSelected = !!selectedSavedAudienceId;

  // Section 4: Placements
  const [advantagePlacements, setAdvantagePlacements] = useState(true);
  const [selectedPlacements, setSelectedPlacements] = useState<string[]>(ALL_PLACEMENTS.map((p) => p.id));

  // Section 5: Budget & Schedule
  const [budgetMode, setBudgetMode] = useState<"cbo" | "adset">("adset");
  const [budget, setBudget] = useState("");
  const [spendLimit, setSpendLimit] = useState("None");
  const [dailyMinimum, setDailyMinimum] = useState("");
  const [dailyMaximum, setDailyMaximum] = useState("");
  const [startTime, setStartTime] = useState<Date | undefined>();
  const [endTime, setEndTime] = useState<Date | undefined>();

  // Section 6: Attribution
  const [clickThrough, setClickThrough] = useState("7");
  const [engagedView, setEngagedView] = useState("1");
  const [viewThrough, setViewThrough] = useState("1");
  const [attributionModel, setAttributionModel] = useState("Standard");

  // Section: Ad Transparency (DSA Compliance)
  const [dsaBeneficiary, setDsaBeneficiary] = useState("");
  const [dsaPayorDifferent, setDsaPayorDifferent] = useState(false);
  const [dsaPayor, setDsaPayor] = useState("");
  const [dsaRecommendations, setDsaRecommendations] = useState<DsaRecommendation[]>([]);
  const [loadingDsaRecommendations, setLoadingDsaRecommendations] = useState(false);
  const [dsaSectionExpanded, setDsaSectionExpanded] = useState(false);

  // ── Derived: available conversion location options ──
  const conversionLocationOptions = useMemo(() => {
    if (!campaignObjective) return [];
    return CONVERSION_LOCATION_OPTIONS[campaignObjective] || [];
  }, [campaignObjective]);

  const isAwareness = campaignObjective === "OUTCOME_AWARENESS";

  // ── Derived: available performance goal options ──
  const performanceGoalOptions = useMemo(() => {
    if (!campaignObjective) return [];
    const objectiveGoals = PERFORMANCE_GOAL_OPTIONS[campaignObjective];
    if (!objectiveGoals) return [];
    if (isAwareness) return objectiveGoals._default || [];
    if (!conversionLocation) return [];
    return objectiveGoals[conversionLocation] || [];
  }, [campaignObjective, conversionLocation, isAwareness]);

  // ── Helpers: conditional field visibility ──
  const requiresPixel = (): boolean => {
    if (!campaignObjective || !conversionLocation) return false;
    if (campaignObjective === "OUTCOME_SALES" && conversionLocation === "WEBSITE") return true;
    if (campaignObjective === "OUTCOME_LEADS" && conversionLocation === "WEBSITE") return true;
    if (
      campaignObjective === "OUTCOME_ENGAGEMENT" &&
      conversionLocation === "WEBSITE" &&
      performanceGoal === "OFFSITE_CONVERSIONS"
    )
      return true;
    return false;
  };

  const requiresPageId = (): boolean => {
    if (!campaignObjective) return false;
    if (campaignObjective === "OUTCOME_AWARENESS") return true;

    const pageRequiredDestinations: Record<string, string[]> = {
      OUTCOME_TRAFFIC: ["MESSENGER", "INSTAGRAM_PROFILE", "PHONE_CALL"],
      OUTCOME_ENGAGEMENT: ["ON_PAGE"],
      OUTCOME_LEADS: ["ON_AD", "MESSENGER", "PHONE_CALL"],
      OUTCOME_SALES: ["MESSENGER", "PHONE_CALL"],
    };

    const destinations = pageRequiredDestinations[campaignObjective] || [];
    if (destinations.includes(conversionLocation)) return true;

    return false;
  };

  const showAttribution = [
    "OFFSITE_CONVERSIONS",
    "VALUE",
    "LEAD_GENERATION",
    "QUALITY_LEAD",
    "MESSAGING_PURCHASE_CONVERSION",
  ].includes(performanceGoal);

  // ── Facebook Pages query from cache ──
  const { data: cachedPages, isLoading: loadingPages } = useQuery({
    queryKey: ["cached-facebook-pages-adset", user?.id, selectedAdAccountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("meta_cached_facebook_pages")
        .select("page_id, page_name, display_name")
        .eq("user_id", user!.id)
        .order("page_name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && !!selectedAdAccountId,
    staleTime: 5 * 60 * 1000,
  });

  // Reset dependent fields when ad account changes
  useEffect(() => {
    setSelectedCampaign("");
    setSelectedPixel("");
    setIncludeAudiences([]);
    setExcludeAudiences([]);
    setSelectedLocations([]);
    setSpecialAdCategory(null);
    setAllowedCountries([]);
    setSelectedSavedAudienceId(null);
    setCampaignObjective(null);
    setConversionLocation("");
    setPerformanceGoal("");
    setSelectedPageId("");
    // Reset DSA fields
    setDsaBeneficiary("");
    setDsaPayor("");
    setDsaPayorDifferent(false);
  }, [selectedAdAccountId]);

  // Fetch DSA defaults and recommendations when ad account changes
  useEffect(() => {
    const fetchDsaData = async () => {
      if (!user?.id || !selectedAdAccountId) {
        setDsaRecommendations([]);
        return;
      }

      setLoadingDsaRecommendations(true);

      try {
        const { data: accountData } = await supabase
          .from("ad_accounts")
          .select("default_dsa_beneficiary, default_dsa_payor")
          .eq("user_id", user.id)
          .eq("ad_account_id", selectedAdAccountId)
          .maybeSingle();

        if (accountData) {
          if (accountData.default_dsa_beneficiary) {
            setDsaBeneficiary(accountData.default_dsa_beneficiary);
          }
          if (accountData.default_dsa_payor && accountData.default_dsa_payor !== accountData.default_dsa_beneficiary) {
            setDsaPayor(accountData.default_dsa_payor);
            setDsaPayorDifferent(true);
          }
        }

        const { data: recommendations } = await supabase
          .from("meta_cached_dsa_recommendations")
          .select("name")
          .eq("user_id", user.id)
          .eq("ad_account_id", selectedAdAccountId);

        const allNames: DsaRecommendation[] = [];
        const seen = new Set<string>();

        if (accountData?.default_dsa_beneficiary && !seen.has(accountData.default_dsa_beneficiary.toLowerCase())) {
          allNames.push({ name: accountData.default_dsa_beneficiary });
          seen.add(accountData.default_dsa_beneficiary.toLowerCase());
        }
        if (accountData?.default_dsa_payor && !seen.has(accountData.default_dsa_payor.toLowerCase())) {
          allNames.push({ name: accountData.default_dsa_payor });
          seen.add(accountData.default_dsa_payor.toLowerCase());
        }

        if (recommendations) {
          for (const rec of recommendations) {
            if (!seen.has(rec.name.toLowerCase())) {
              allNames.push({ name: rec.name });
              seen.add(rec.name.toLowerCase());
            }
          }
        }

        setDsaRecommendations(allNames);

        supabase.functions
          .invoke("n8n-proxy", {
            body: {
              action: "GET_DSA_RECOMMENDATIONS",
              payload: { ad_account_id: selectedAdAccountId },
            },
          })
          .catch((err) => console.log("DSA sync triggered (non-blocking):", err));
      } catch (error) {
        console.error("Error fetching DSA data:", error);
      } finally {
        setLoadingDsaRecommendations(false);
      }
    };

    fetchDsaData();
  }, [user?.id, selectedAdAccountId]);

  // Fetch campaign details (objective + SAC) when campaign is selected
  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!selectedCampaign) {
        setSpecialAdCategory(null);
        setAllowedCountries([]);
        setCampaignObjective(null);
        // Reset dependent fields
        setConversionLocation("");
        setPerformanceGoal("");
        setSelectedPixel("");
        setCustomEventType("PURCHASE");
        setSelectedPageId("");
        return;
      }

      const { data, error } = await supabase
        .from("meta_cached_campaigns")
        .select("special_ad_category, special_ad_category_country, objective")
        .eq("campaign_id", selectedCampaign)
        .maybeSingle();

      if (data && !error) {
        const category =
          data.special_ad_category && data.special_ad_category !== "NONE"
            ? (data.special_ad_category as SpecialAdCategory)
            : null;
        setSpecialAdCategory(category);

        const countries = data.special_ad_category_country;
        setAllowedCountries(countries || []);

        // Set objective
        const obj = data.objective || null;
        setCampaignObjective(obj);

        // Reset dependent fields on campaign change
        setConversionLocation("");
        setPerformanceGoal("");
        setSelectedPixel("");
        // Set default conversion event for the new objective
        const eventConfig = obj ? CONVERSION_EVENTS_BY_OBJECTIVE[obj] : null;
        setCustomEventType(eventConfig?.default || "PURCHASE");
        setSelectedPageId("");
      } else {
        setSpecialAdCategory(null);
        setAllowedCountries([]);
        setCampaignObjective(null);
        setConversionLocation("");
        setPerformanceGoal("");
        setSelectedPixel("");
        setCustomEventType("PURCHASE");
        setSelectedPageId("");
      }
    };

    fetchCampaignDetails();
  }, [selectedCampaign]);

  // Auto-select first performance goal when conversion location changes
  useEffect(() => {
    if (performanceGoalOptions.length > 0) {
      setPerformanceGoal(performanceGoalOptions[0].value);
    } else {
      setPerformanceGoal("");
    }
  }, [conversionLocation, campaignObjective]);

  // For Awareness: auto-select first performance goal when objective changes
  useEffect(() => {
    if (isAwareness && performanceGoalOptions.length > 0) {
      setPerformanceGoal(performanceGoalOptions[0].value);
    }
  }, [isAwareness, performanceGoalOptions]);

  // Force age/gender values when SAC is active
  useEffect(() => {
    if (specialAdCategory) {
      setAgeMin(18);
      setAgeMax(65);
      setGenders("All");
    }
  }, [specialAdCategory]);

  // Auto-detect budget mode from selected campaign's budget_type
  useEffect(() => {
    if (!selectedCampaign) {
      setBudgetMode("adset");
      return;
    }
    const campaign = campaigns.find((c) => c.id === selectedCampaign);
    if (campaign) {
      const mode = campaign.budget_type === "CBO" ? "cbo" : "adset";
      setBudgetMode(mode);
      if (mode === "adset") {
        setSpendLimit("None");
      }
    }
  }, [selectedCampaign, campaigns]);

  // Fetch campaigns when ad account changes
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user?.id || !selectedAdAccountId) {
        setCampaigns([]);
        return;
      }

      setLoadingCampaigns(true);
      setCampaigns([]);

      try {
        const { data, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
          body: {
            action: "GET_CAMPAIGNS",
            payload: { ad_account_id: selectedAdAccountId },
          },
        });

        if (proxyError) throw new Error("Failed to fetch campaigns");
        const campaignList = Array.isArray(data) ? data : data.data || [];

        interface CampaignResponse {
          campaign_id?: string;
          id?: string;
          campaign_name?: string;
          name?: string;
          status?: string;
          display_name?: string;
          budget_type?: "CBO" | "Ad Set Budget" | string;
        }

        const mappedCampaigns = campaignList.map((c: CampaignResponse) => ({
          id: c.campaign_id || c.id || "",
          name: c.display_name || c.campaign_name || c.name || "",
          budget_type: (c.budget_type === "CBO" ? "CBO" : "Ad Set Budget") as "CBO" | "Ad Set Budget",
        }));

        setCampaigns(mappedCampaigns);
      } catch (error) {
        console.error("CreateAdSet: Error fetching campaigns:", error);
        toast({
          title: "Error",
          description: "Failed to load campaigns",
          variant: "destructive",
        });
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [user?.id, selectedAdAccountId, toast]);

  // Fetch custom audiences when ad account changes
  useEffect(() => {
    const fetchAudiences = async () => {
      if (!user?.id || !selectedAdAccountId) {
        setCustomAudiences([]);
        return;
      }

      setLoadingAudiences(true);
      try {
        const { data, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
          body: {
            action: "GET_CUSTOM_AUDIENCES",
            payload: { ad_account_id: selectedAdAccountId },
          },
        });

        if (proxyError) throw new Error("Failed to fetch custom audiences");

        if (!data) {
          setCustomAudiences([]);
          return;
        }
        const audiences = Array.isArray(data) ? data : data.data || [];

        interface AudienceResponse {
          audience_id?: string;
          id?: string;
          audience_name?: string;
          name?: string;
          display_name?: string;
        }

        setCustomAudiences(
          audiences.map((a: AudienceResponse) => ({
            id: a.audience_id || a.id || "",
            name: a.audience_name || a.name || "",
            display_name: a.display_name || `${a.audience_name || a.name} (${a.audience_id || a.id})`,
          })),
        );
      } catch (error) {
        console.error("Error fetching custom audiences:", error);
      } finally {
        setLoadingAudiences(false);
      }
    };

    fetchAudiences();
  }, [user?.id, selectedAdAccountId]);

  // Fetch pixels when ad account changes
  useEffect(() => {
    const fetchPixels = async () => {
      if (!user?.id || !selectedAdAccountId) {
        setPixels([]);
        return;
      }

      setLoadingPixels(true);
      try {
        const { data, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
          body: {
            action: "GET_PIXELS",
            payload: { ad_account_id: selectedAdAccountId },
          },
        });

        if (proxyError) throw new Error("Failed to fetch pixels");
        const pixelList = Array.isArray(data) ? data : data.data || [];

        interface PixelResponse {
          pixel_id?: string;
          id?: string;
          pixel_name?: string;
          name?: string;
          display_name?: string;
        }

        setPixels(
          pixelList.map((p: PixelResponse) => ({
            id: p.pixel_id || p.id || "",
            name: p.display_name || p.pixel_name || p.name || "",
          })),
        );
      } catch (error) {
        console.error("Error fetching pixels:", error);
      } finally {
        setLoadingPixels(false);
      }
    };

    fetchPixels();
  }, [user?.id, selectedAdAccountId]);

  // Handle Advantage+ placements toggle
  useEffect(() => {
    if (advantagePlacements) {
      setSelectedPlacements(ALL_PLACEMENTS.map((p) => p.id));
    }
  }, [advantagePlacements]);

  // EU country detection based on selected locations
  const hasEuTargeting = useMemo(() => {
    if (selectedLocations.length === 0) return false;
    return selectedLocations.some((location) => {
      const countryCode = location.country_code?.toUpperCase();
      if (countryCode && EU_COUNTRY_CODES.includes(countryCode)) return true;
      if (location.type === "country" && EU_COUNTRY_CODES.includes(location.key?.toUpperCase())) return true;
      return false;
    });
  }, [selectedLocations]);

  // Auto-expand DSA section when EU targeting detected
  useEffect(() => {
    if (hasEuTargeting) {
      setDsaSectionExpanded(true);
    }
  }, [hasEuTargeting]);

  // DSA recommendations as combobox options
  const dsaOptions: ComboboxOption[] = useMemo(() => {
    const options: ComboboxOption[] = [];
    const seen = new Set<string>();

    const sortedRecs = [...dsaRecommendations].map((r) => r.name).sort((a, b) => a.localeCompare(b));

    for (const name of sortedRecs) {
      const lowerName = name.toLowerCase();
      if (!seen.has(lowerName)) {
        options.push({ value: name, label: name });
        seen.add(lowerName);
      }
    }

    return options;
  }, [dsaRecommendations]);

  const togglePlacement = (placementId: string) => {
    if (advantagePlacements) return;
    setSelectedPlacements((prev) =>
      prev.includes(placementId) ? prev.filter((id) => id !== placementId) : [...prev, placementId],
    );
  };

  const getSelectedPlatforms = () => {
    const platforms = new Set<string>();
    selectedPlacements.forEach((placementId) => {
      const placement = ALL_PLACEMENTS.find((p) => p.id === placementId);
      if (placement) platforms.add(placement.platform);
    });
    return Array.from(platforms);
  };

  // Legacy mapping for backward compatibility
  const getLegacyConversionLocation = (): string => {
    if (isAwareness) return "";
    if (conversionLocation === "WEBSITE") return "Single: Website";
    return conversionLocation;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAdAccountId) {
      toast({
        title: "Validation Error",
        description: "Please select an ad account from the sidebar",
        variant: "destructive",
      });
      return;
    }
    if (!selectedCampaign) {
      toast({ title: "Validation Error", description: "Please select a campaign", variant: "destructive" });
      return;
    }
    if (!adSetName.trim()) {
      toast({ title: "Validation Error", description: "Ad set name is required", variant: "destructive" });
      return;
    }
    // Performance goal always required
    if (!performanceGoal) {
      toast({ title: "Validation Error", description: "Please select a performance goal", variant: "destructive" });
      return;
    }
    // Conversion location required for non-Awareness
    if (!isAwareness && !conversionLocation) {
      toast({ title: "Validation Error", description: "Please select a conversion location", variant: "destructive" });
      return;
    }
    // Pixel only required when needed
    if (requiresPixel() && !selectedPixel) {
      toast({ title: "Validation Error", description: "Please select a dataset (pixel)", variant: "destructive" });
      return;
    }
    // Page ID only required when needed
    if (requiresPageId() && !selectedPageId) {
      toast({ title: "Validation Error", description: "Please select a Facebook Page", variant: "destructive" });
      return;
    }
    // Only validate targeting fields if NO saved audience is selected
    if (!isSavedAudienceSelected) {
      if (selectedLocations.length === 0) {
        toast({ title: "Validation Error", description: "At least one location is required", variant: "destructive" });
        return;
      }
      if (ageMin >= ageMax) {
        toast({ title: "Validation Error", description: "Age Min must be less than Age Max", variant: "destructive" });
        return;
      }
    }
    if (budgetMode === "adset" && (!budget || parseFloat(budget) <= 0)) {
      toast({ title: "Validation Error", description: "Budget must be greater than 0", variant: "destructive" });
      return;
    }
    // Validate DSA fields when targeting EU countries
    if (hasEuTargeting && !dsaBeneficiary.trim()) {
      toast({
        title: "Validation Error",
        description: "DSA Beneficiary is required when targeting EU countries",
        variant: "destructive",
      });
      return;
    }
    if (hasEuTargeting && dsaPayorDifferent && !dsaPayor.trim()) {
      toast({
        title: "Validation Error",
        description: "DSA Payor is required when targeting EU countries",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      const campaignInfo = campaigns.find((c) => c.id === selectedCampaign);
      const pixelInfo = pixels.find((p) => p.id === selectedPixel);

      const includeAudienceStr = includeAudiences
        .map((id) => {
          const aud = customAudiences.find((a) => a.id === id);
          return aud ? aud.display_name : id;
        })
        .join(", ");

      const excludeAudienceStr = excludeAudiences
        .map((id) => {
          const aud = customAudiences.find((a) => a.id === id);
          return aud ? aud.display_name : id;
        })
        .join(", ");

      const placementLabels = selectedPlacements
        .map((id) => {
          const p = ALL_PLACEMENTS.find((pl) => pl.id === id);
          return p ? p.label : id;
        })
        .join(", ");

      const payload: Record<string, any> = {
        ad_account: `${selectedAdAccountName} (${selectedAdAccountId})`,
        campaign: campaignInfo?.name || selectedCampaign,
        ad_set_name: adSetName,
        status,
        // Legacy field (backward compat)
        conversion_location: getLegacyConversionLocation(),
        // New fields
        campaign_objective: campaignObjective,
        destination_type: isAwareness ? undefined : conversionLocation || undefined,
        optimization_goal: performanceGoal,
        page_id: requiresPageId() ? selectedPageId || undefined : undefined,
        dataset: requiresPixel() ? pixelInfo?.name || selectedPixel || undefined : undefined,
        custom_event_type: requiresPixel() ? customEventType : undefined,
        // Legacy field
        performance_goal: performanceGoal,
        geo_locations: isSavedAudienceSelected ? undefined : formatGeoTargeting(selectedLocations).geo_locations,
        age_min: isSavedAudienceSelected ? undefined : ageMin,
        age_max: isSavedAudienceSelected ? undefined : ageMax,
        genders: isSavedAudienceSelected ? undefined : genders,
        include_custom_audience: isSavedAudienceSelected ? undefined : includeAudienceStr,
        exclude_custom_audience: isSavedAudienceSelected ? undefined : excludeAudienceStr,
        saved_audience_id: selectedSavedAudienceId || undefined,
        placements: placementLabels,
        platforms: JSON.stringify(getSelectedPlatforms()),
        budget_type: budgetMode === "cbo" ? "CBO" : "Ad Set Budget",
        budget: budgetMode === "adset" ? parseFloat(budget) || 0 : 0,
        spend_limit: budgetMode === "cbo" ? spendLimit : "None",
        daily_minimum: budgetMode === "cbo" && spendLimit === "Value" ? parseFloat(dailyMinimum) || 0 : 0,
        daily_maximum: budgetMode === "cbo" && spendLimit === "Value" ? parseFloat(dailyMaximum) || 0 : 0,
        start_time: startTime ? startTime.toISOString() : "",
        end_time: endTime ? endTime.toISOString() : "",
        click_through: showAttribution ? parseInt(clickThrough) : undefined,
        engaged_view: showAttribution ? (engagedView === "None" ? 0 : parseInt(engagedView)) : undefined,
        view_through: showAttribution ? (viewThrough === "None" ? 0 : parseInt(viewThrough)) : undefined,
        attribution_model: showAttribution ? attributionModel : undefined,
        dsa_beneficiary: dsaBeneficiary.trim() || null,
        dsa_payor: dsaPayorDifferent ? dsaPayor.trim() || null : dsaBeneficiary.trim() || null,
      };

      const { data, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
        body: {
          action: "CREATE_ADSET",
          payload,
        },
      });

      if (proxyError) {
        throw new Error(proxyError.message || "Unknown error");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Success",
        description: "Ad Set created successfully!",
      });

      // Reset form
      setSelectedCampaign("");
      setAdSetName("");
      setStatus("PAUSED");
      setSelectedPixel("");
      setSelectedLocations([]);
      setBudget("");
      setIncludeAudiences([]);
      setExcludeAudiences([]);
      setSelectedSavedAudienceId(null);
      setCampaignObjective(null);
      setConversionLocation("");
      setPerformanceGoal("");
      setSelectedPageId("");
      setDsaBeneficiary("");
      setDsaPayor("");
      setDsaPayorDifferent(false);
      setDsaSectionExpanded(false);
    } catch (error) {
      console.error("Error creating ad set:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create ad set",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isDataLoading = loadingCampaigns || loadingPixels;

  // Show message if no ad account selected
  if (!selectedAdAccountId) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Create Ad Set</h1>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select an Ad Account from the sidebar to create an ad set.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Create Ad Set</h1>
        <p className="text-muted-foreground">
          Ad Account: {selectedAdAccountName} ({selectedAdAccountId})
        </p>
      </div>

      <ScrollArea className="h-[calc(100vh-180px)]">
        <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl pr-4">
          {/* Section 1: Basic Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Basic Information</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="campaign">Campaign *</Label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign} disabled={loadingCampaigns}>
                  <SelectTrigger id="campaign">
                    <SelectValue placeholder={loadingCampaigns ? "Loading..." : "Select campaign"} />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adSetName">Ad Set Name *</Label>
                <Input
                  id="adSetName"
                  value={adSetName}
                  onChange={(e) => setAdSetName(e.target.value)}
                  placeholder="Enter ad set name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <RadioGroup value={status} onValueChange={setStatus} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ACTIVE" id="active" />
                  <Label htmlFor="active" className="font-normal">
                    Active
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="PAUSED" id="paused" />
                  <Label htmlFor="paused" className="font-normal">
                    Paused
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Show detected objective */}
            {campaignObjective && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="h-4 w-4" />
                Campaign Objective:{" "}
                <span className="font-medium text-foreground">{campaignObjective.replace("OUTCOME_", "")}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Section 2: Conversion & Performance */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Conversion & Performance</h2>

            {/* Conversion Location — hidden for Awareness */}
            {!isAwareness && (
              <div className="space-y-2">
                <Label>Conversion Location *</Label>
                <Select
                  value={conversionLocation}
                  onValueChange={(val) => {
                    setConversionLocation(val);
                    setPerformanceGoal("");
                    setSelectedPixel("");
                    setSelectedPageId("");
                  }}
                  disabled={!campaignObjective || conversionLocationOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={!campaignObjective ? "Select a campaign first" : "Select conversion location"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {conversionLocationOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Facebook Page — conditional */}
            {requiresPageId() && (
              <div className="space-y-2">
                <Label>Facebook Page *</Label>
                <Select
                  value={selectedPageId}
                  onValueChange={setSelectedPageId}
                  disabled={loadingPages || !cachedPages?.length}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingPages
                          ? "Loading pages..."
                          : cachedPages?.length
                            ? "Select a Facebook Page"
                            : "No pages available"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(cachedPages || []).map((page) => (
                      <SelectItem key={page.page_id} value={page.page_id}>
                        {page.display_name || `${page.page_name} (${page.page_id})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Dataset (Pixel) + Custom Event Type — conditional */}
            {requiresPixel() && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dataset (Pixel) *</Label>
                  <Select value={selectedPixel} onValueChange={setSelectedPixel} disabled={loadingPixels}>
                    <SelectTrigger>
                      <SelectValue placeholder={loadingPixels ? "Loading..." : "Select pixel"} />
                    </SelectTrigger>
                    <SelectContent>
                      {pixels.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Custom Event Type</Label>
                  <Select value={customEventType} onValueChange={setCustomEventType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(allowedConversionEvents?.events || []).map((evt) => (
                        <SelectItem key={evt} value={evt}>
                          {CONVERSION_EVENT_LABELS[evt] || evt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Performance Goal — always shown when objective is set */}
            {campaignObjective && (
              <div className="space-y-2">
                <Label>Performance Goal *</Label>
                <Select
                  value={performanceGoal}
                  onValueChange={setPerformanceGoal}
                  disabled={performanceGoalOptions.length === 0 || (campaignObjective === "OUTCOME_ENGAGEMENT" && conversionLocation === "ON_PAGE")}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !campaignObjective
                          ? "Select a campaign first"
                          : !isAwareness && !conversionLocation
                            ? "Select a conversion location first"
                            : "Select performance goal"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {performanceGoalOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator />

          {/* Section 3: Targeting */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Targeting</h2>

            {/* Saved Audience Dropdown */}
            <div className="space-y-2">
              <Label>Saved Audience (Optional)</Label>
              <p className="text-xs text-muted-foreground">
                Select a saved audience to use its targeting. This will override manual targeting settings below.
              </p>
              <Select
                value={selectedSavedAudienceId || "__none__"}
                onValueChange={(value) => setSelectedSavedAudienceId(value === "__none__" ? null : value)}
                disabled={loadingSavedAudiences || !selectedAdAccountId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !selectedAdAccountId
                        ? "Select ad account first"
                        : loadingSavedAudiences
                          ? "Loading saved audiences..."
                          : "Select a saved audience (optional)"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (use manual targeting)</SelectItem>
                  {(savedAudiences || []).map((aud) => (
                    <SelectItem key={aud.audience_id} value={aud.audience_id}>
                      {aud.audience_name} ({aud.approximate_count || "N/A"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSavedAudienceSelected && (
                <Alert className="mt-2 bg-primary/5 border-primary/20">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Using saved audience targeting. Manual targeting fields below are disabled.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>Locations *</Label>
              {specialAdCategory && !isSavedAudienceSelected && (
                <p className="text-sm text-amber-500">
                  Special Ad Category: {specialAdCategory}
                  {allowedCountries.length > 0 && <span> — Limited to: {allowedCountries.join(", ")}</span>}
                </p>
              )}
              <div className={isSavedAudienceSelected ? "opacity-50 pointer-events-none" : ""}>
                <LocationSelector
                  value={selectedLocations}
                  onChange={setSelectedLocations}
                  specialAdCategory={specialAdCategory}
                  allowedCountries={allowedCountries.length > 0 ? allowedCountries : undefined}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ageMin" className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>
                  Age Min
                </Label>
                <Select
                  value={String(ageMin)}
                  onValueChange={(v) => setAgeMin(parseInt(v))}
                  disabled={!!specialAdCategory || isSavedAudienceSelected}
                >
                  <SelectTrigger
                    className={specialAdCategory || isSavedAudienceSelected ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 48 }, (_, i) => i + 18).map((age) => (
                      <SelectItem key={age} value={String(age)} disabled={age > ageMax}>
                        {age}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ageMax" className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>
                  Age Max
                </Label>
                <Select
                  value={String(ageMax)}
                  onValueChange={(v) => setAgeMax(parseInt(v))}
                  disabled={!!specialAdCategory || isSavedAudienceSelected}
                >
                  <SelectTrigger
                    className={specialAdCategory || isSavedAudienceSelected ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 48 }, (_, i) => i + 18).map((age) => (
                      <SelectItem key={age} value={String(age)} disabled={age < ageMin}>
                        {age === 65 ? "65+" : age}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>Genders</Label>
                <Select
                  value={genders}
                  onValueChange={setGenders}
                  disabled={!!specialAdCategory || isSavedAudienceSelected}
                >
                  <SelectTrigger
                    className={specialAdCategory || isSavedAudienceSelected ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className={cn("grid grid-cols-2 gap-4", isSavedAudienceSelected && "opacity-50 pointer-events-none")}>
              <div className="space-y-2">
                <Label className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>
                  Include Custom Audiences
                </Label>
                <SearchableMultiSelect
                  options={customAudiences
                    .filter((a) => !excludeAudiences.includes(a.id))
                    .map((a) => ({
                      value: a.id,
                      label: a.name,
                      displayName: a.display_name,
                    }))}
                  selectedValues={includeAudiences}
                  onSelectionChange={setIncludeAudiences}
                  placeholder={
                    isSavedAudienceSelected
                      ? "Disabled (using saved audience)"
                      : !selectedAdAccountId
                        ? "Select ad account first"
                        : loadingAudiences
                          ? "Loading audiences..."
                          : customAudiences.length === 0
                            ? "No audiences available"
                            : "Add audience"
                  }
                  searchPlaceholder="Search audiences..."
                  emptyMessage="No audiences found"
                  disabled={loadingAudiences || !selectedAdAccountId || isSavedAudienceSelected}
                  badgeVariant="primary"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {includeAudiences.map((id) => {
                    const aud = customAudiences.find((a) => a.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded text-sm"
                      >
                        {aud?.name || id}
                        <button
                          type="button"
                          className="hover:text-destructive ml-1"
                          onClick={() => setIncludeAudiences((prev) => prev.filter((i) => i !== id))}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label className={isSavedAudienceSelected ? "text-muted-foreground" : ""}>
                  Exclude Custom Audiences
                </Label>
                <SearchableMultiSelect
                  options={customAudiences
                    .filter((a) => !includeAudiences.includes(a.id))
                    .map((a) => ({
                      value: a.id,
                      label: a.name,
                      displayName: a.display_name,
                    }))}
                  selectedValues={excludeAudiences}
                  onSelectionChange={setExcludeAudiences}
                  placeholder={
                    isSavedAudienceSelected
                      ? "Disabled (using saved audience)"
                      : !selectedAdAccountId
                        ? "Select ad account first"
                        : loadingAudiences
                          ? "Loading audiences..."
                          : customAudiences.length === 0
                            ? "No audiences available"
                            : "Add audience"
                  }
                  searchPlaceholder="Search audiences..."
                  emptyMessage="No audiences found"
                  disabled={loadingAudiences || !selectedAdAccountId || isSavedAudienceSelected}
                  badgeVariant="destructive"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {excludeAudiences.map((id) => {
                    const aud = customAudiences.find((a) => a.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 bg-destructive/10 text-destructive border border-destructive/20 px-2 py-1 rounded text-sm"
                      >
                        {aud?.name || id}
                        <button
                          type="button"
                          className="hover:text-foreground ml-1"
                          onClick={() => setExcludeAudiences((prev) => prev.filter((i) => i !== id))}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Section: Ad Transparency (DSA Compliance) */}
          <Collapsible open={dsaSectionExpanded} onOpenChange={setDsaSectionExpanded}>
            <div className="space-y-4">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">Beneficiary & Payer</h2>
                    {hasEuTargeting && (
                      <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full border border-amber-500/30">
                        Required for EU
                      </span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Required for ads targeting EU countries under the Digital Services Act (DSA). Specifies who
                          benefits from and pays for the ad.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    {!hasEuTargeting && <span className="text-xs text-muted-foreground">Optional</span>}
                    {dsaSectionExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-4">
                {/* Beneficiary */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Who benefits from this ad?
                    {hasEuTargeting && <span className="text-destructive">*</span>}
                  </Label>
                  {loadingDsaRecommendations ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading recommendations...
                    </div>
                  ) : (
                    <Combobox
                      options={dsaOptions}
                      value={dsaBeneficiary}
                      onValueChange={setDsaBeneficiary}
                      placeholder="Select or type beneficiary name"
                      searchPlaceholder="Search or enter name..."
                      emptyMessage="Type to add a custom name"
                      disabled={!selectedAdAccountId}
                    />
                  )}
                  <p className="text-xs text-muted-foreground">
                    Max 512 characters. The person or organization that benefits from this ad. If you don't select a
                    value, the default will be applied automatically.
                  </p>
                </div>

                {/* Payor Toggle */}
                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="payor-toggle" className="flex-1 cursor-pointer">
                    The person or organization paying for this ad is different from the beneficiary
                  </Label>
                  <Switch id="payor-toggle" checked={dsaPayorDifferent} onCheckedChange={setDsaPayorDifferent} />
                </div>

                {/* Payor (conditional) */}
                {dsaPayorDifferent && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Who is paying for this ad?
                      {hasEuTargeting && <span className="text-destructive">*</span>}
                    </Label>
                    {loadingDsaRecommendations ? (
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading recommendations...
                      </div>
                    ) : (
                      <Combobox
                        options={dsaOptions}
                        value={dsaPayor}
                        onValueChange={setDsaPayor}
                        placeholder="Select or type payor name"
                        searchPlaceholder="Search or enter name..."
                        emptyMessage="Type to add a custom name"
                        disabled={!selectedAdAccountId}
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      Max 512 characters. The person or organization paying for this ad.
                    </p>
                  </div>
                )}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Section 4: Placements */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Placements</h2>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="advantagePlacements"
                checked={advantagePlacements}
                onCheckedChange={(checked) => setAdvantagePlacements(!!checked)}
              />
              <Label htmlFor="advantagePlacements">Use Advantage+ Placements (recommended)</Label>
            </div>

            {!advantagePlacements && (
              <div className="grid grid-cols-2 gap-4">
                {["facebook", "instagram", "messenger", "threads", "audience_network"].map((platform) => (
                  <div key={platform} className="space-y-2">
                    <h3 className="font-medium capitalize">{platform.replace("_", " ")}</h3>
                    {ALL_PLACEMENTS.filter((p) => p.platform === platform).map((placement) => (
                      <div key={placement.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={placement.id}
                          checked={selectedPlacements.includes(placement.id)}
                          onCheckedChange={() => togglePlacement(placement.id)}
                        />
                        <Label htmlFor={placement.id} className="font-normal text-sm">
                          {placement.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Section 5: Budget & Schedule */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Budget & Schedule</h2>

            {/* CBO Info Banner */}
            {budgetMode === "cbo" && (
              <Alert className="border-blue-500/30 bg-blue-500/10">
                <Info className="h-4 w-4 text-blue-400" />
                <AlertDescription className="text-sm">
                  This campaign uses <strong>Campaign Budget Optimization (CBO)</strong>. Budget is controlled at the
                  campaign level. You can optionally set spend limits for this ad set.
                </AlertDescription>
              </Alert>
            )}

            {/* Ad Set Budget Mode: Show Daily Budget input */}
            {budgetMode === "adset" && (
              <div className="space-y-2">
                <Label htmlFor="budget">Daily Budget *</Label>
                <Input
                  id="budget"
                  type="number"
                  step="0.01"
                  min="0"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="Enter daily budget"
                />
              </div>
            )}

            {/* CBO Mode: Show optional Spend Limits */}
            {budgetMode === "cbo" && (
              <>
                <div className="space-y-2">
                  <Label>Ad Set Spend Limits</Label>
                  <RadioGroup value={spendLimit} onValueChange={setSpendLimit} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="None" id="spendNone" />
                      <Label htmlFor="spendNone" className="font-normal">
                        None
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Value" id="spendValue" />
                      <Label htmlFor="spendValue" className="font-normal">
                        Set daily min/max
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {spendLimit === "Value" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dailyMin">Daily Minimum ($)</Label>
                      <Input
                        id="dailyMin"
                        type="number"
                        step="0.01"
                        min="0"
                        value={dailyMinimum}
                        onChange={(e) => setDailyMinimum(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dailyMax">Daily Maximum ($)</Label>
                      <Input
                        id="dailyMax"
                        type="number"
                        step="0.01"
                        min="0"
                        value={dailyMaximum}
                        onChange={(e) => setDailyMaximum(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startTime && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startTime ? format(startTime, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={startTime} onSelect={setStartTime} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !endTime && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endTime ? format(endTime, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={endTime} onSelect={setEndTime} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {showAttribution && (
            <>
              <Separator />

              {/* Section 6: Attribution */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold border-b pb-2">Attribution</h2>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Click-through (days)</Label>
                    <Select value={clickThrough} onValueChange={setClickThrough}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 day</SelectItem>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="28">28 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Engaged View (days)</Label>
                    <Select value={engagedView} onValueChange={setEngagedView}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="1">1 day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>View-through (days)</Label>
                    <Select value={viewThrough} onValueChange={setViewThrough}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="1">1 day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Attribution Model</Label>
                  <Select value={attributionModel} onValueChange={setAttributionModel}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard (Last Touch)</SelectItem>
                      <SelectItem value="Data-driven">Incremental (Data-Driven)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={submitting} className="flex-1">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Ad Set"
              )}
            </Button>
          </div>
        </form>
      </ScrollArea>
    </div>
  );
};

export default CreateAdSet;
